import React, { useEffect, useState } from 'react';
import { ExternalLink, FileStack, Loader2, X } from 'lucide-react';
import { errorText } from '../ErrorBanner';
import { ChecklistItem, CompanyDoc, RecordRow, submissionApi } from '../../services/submissionApi';

const STATE: Record<CompanyDoc['state'], [string, string]> = {
  valid: ['유효', 'bg-green-50 text-green-700'], expiring: ['곧 만료', 'bg-amber-50 text-amber-700'],
  expired: ['만료', 'bg-red-50 text-red-600'], 'per-bid': ['입찰 때 발급', 'bg-slate-100 text-slate-600'],
};
/** 오늘은 유효하지만 제출 기한에는 만료된 서류 — 입찰에 내면 자격 미달이다 */
const expiresBeforeDue = (d: CompanyDoc, dueAt: string | null): boolean =>
  !!dueAt && !!d.expiresAt && d.expiresAt < dueAt && d.state !== 'expired';

const label = (d: CompanyDoc) => `${d.name}${d.issuedAt ? ` · 발급 ${d.issuedAt}` : ''}${d.expiresAt ? ` · 만료 ${d.expiresAt}` : ''}`;

/**
 * 제출서류 묶음(작업 7, #125, 2026-10-22) — 접수서의 요구 서류마다 회사서류(#159)를 고르고,
 * 직접 준비한 파일(제안서 · 서식 등)을 더해 PDF 한 개로 묶어 「07.제출서류」 에 올린다. 만료된 서류는 못 고른다.
 */
export const SubmissionPanel: React.FC<{ projectId: number }> = ({ projectId }) => {
  const [items, setItems] = useState<ChecklistItem[] | null>(null);
  const [others, setOthers] = useState<CompanyDoc[]>([]);
  /* 마감(제출 기한) — 오늘 유효해도 이 날 전에 만료되는 서류는 자격 미달이다(2026-10-27) */
  const [dueAt, setDueAt] = useState<string | null>(null);
  const [pick, setPick] = useState<Record<number, number | null>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ fileName: string; url: string; pages: number; warnings: string[] } | null>(null);
  // 실적 — 아카이브 계약을 실적 요건에 대 본 것. 고른 것은 실적표 · 증빙(계약서)으로
  const [rec, setRec] = useState<{ requirement: { years: number | null; minAmount: number | null; text: string }; records: RecordRow[] } | null>(null);
  const [chosen, setChosen] = useState<number[]>([]);
  const [onlyFit, setOnlyFit] = useState(true);
  const [withEvidence, setWithEvidence] = useState(true);
  const [table, setTable] = useState<{ fileName: string; url: string } | null>(null);
  useEffect(() => { submissionApi.records(projectId).then(setRec).catch(() => setRec(null)); }, [projectId]);
  const makeTable = async () => {
    setBusy(true); setError(null);
    try { setTable(await submissionApi.recordsTable(projectId, chosen)); }
    catch (e) { setError(errorText(e, '실적표를 만들지 못했습니다')); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    submissionApi.checklist(projectId).then((r) => {
      setItems(r.items); setOthers(r.companyDocs); setDueAt(r.dueAt);
      // 요구 서류마다 쓸 수 있는 첫 서류를 미리 고른다
      const usable = (d: CompanyDoc) => d.state !== 'expired' && !(r.dueAt && d.expiresAt && d.expiresAt < r.dueAt);
      setPick(Object.fromEntries(r.items.map((it, i) => [i, (it.docs.find(usable) ?? it.docs.find((d) => d.state !== 'expired'))?.id ?? null])));
    }).catch((e) => setError(errorText(e, '제출서류를 불러오지 못했습니다')));
  }, [projectId]);

  const order = [
    ...Object.values(pick).filter((v): v is number => v != null).filter((v, i, a) => a.indexOf(v) === i).map((id) => `company:${id}`),
    ...(withEvidence ? chosen.map((id) => `record:${id}`) : []),
    ...files.map((_, i) => `upload:${i}`),
  ];
  const make = async () => {
    setBusy(true); setError(null);
    try { setDone(await submissionApi.bundle(projectId, order, files)); }
    catch (e) { setError(errorText(e, '묶지 못했습니다')); }
    finally { setBusy(false); }
  };

  return (
    <section className="mt-4 bg-white rounded-lg border border-slate-200">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">제출서류</h2>
        <span className="text-xs text-slate-400">요구 서류마다 회사 서류를 고르고 PDF 한 개로 묶어 「07.제출서류」 에 올립니다</span>
      </div>
      <div className="px-4 py-3 space-y-3">
        {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        {!items ? <p className="text-xs text-slate-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> 불러오는 중...</p> : (
          <>
            {items.length === 0 ? (
              <p className="text-sm text-slate-500">접수서에 요구 서류가 없습니다. 검토 탭 접수서의 「제출 서류」 칸을 채우면 여기에 뜹니다.</p>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-400 border-b border-slate-100"><th className="text-left font-medium py-1.5 w-2/5">요구 서류</th><th className="text-left font-medium">회사 서류</th></tr></thead>
                <tbody>
                  {items.map((it, i) => {
                    const chosen = it.docs.find((d) => d.id === pick[i]);
                    const pool = it.docs.length ? it.docs : others;
                    return (
                      <tr key={i} className="border-b border-slate-50 align-top">
                        <td className="py-2 pr-3 text-slate-800">{it.text}</td>
                        <td className="py-1.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <select value={pick[i] ?? ''} onChange={(e) => setPick({ ...pick, [i]: e.target.value ? Number(e.target.value) : null })}
                              className="min-w-0 max-w-full px-2 py-1 text-sm border border-slate-300 rounded">
                              <option value="">{it.types.length ? '넣지 않음' : '직접 준비 — 아래에서 파일로'}</option>
                              {pool.map((d) => <option key={d.id} value={d.id} disabled={d.state === 'expired'}>{label(d)}{d.state === 'expired' ? ' (만료)' : ''}</option>)}
                            </select>
                            {chosen && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATE[chosen.state][1]}`}>{STATE[chosen.state][0]}</span>}
                            {chosen && expiresBeforeDue(chosen, dueAt) && (
                              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600" title={`마감 ${dueAt} 전에 만료됩니다 — 다시 발급해 올리세요`}>마감 전 만료</span>
                            )}
                            {it.types.length > 0 && !it.docs.length && <span className="text-xs text-amber-700">「회사서류」에 이 종류 서류가 없습니다</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
            {rec && (
              <div className="rounded-lg border border-slate-200">
                <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100">
                  <span className="text-sm font-medium text-slate-800">실적</span>
                  <span className="text-xs text-slate-500">
                    {rec.requirement.years || rec.requirement.minAmount
                      ? `요건: ${rec.requirement.years ? `최근 ${rec.requirement.years}년` : ''}${rec.requirement.years && rec.requirement.minAmount ? ' · ' : ''}${rec.requirement.minAmount ? `${rec.requirement.minAmount.toLocaleString('ko-KR')}원 이상(공급가액)` : ''}`
                      : '실적 요건을 못 읽었습니다 — 접수서 「실적 요건」 칸을 채우면 걸러집니다'}
                  </span>
                  <label className="ml-auto text-xs text-slate-600 flex items-center gap-1"><input type="checkbox" checked={onlyFit} onChange={(e) => setOnlyFit(e.target.checked)} /> 충족만</label>
                  <label className="text-xs text-slate-600 flex items-center gap-1"><input type="checkbox" checked={withEvidence} onChange={(e) => setWithEvidence(e.target.checked)} /> 계약서를 증빙으로 묶음에</label>
                  <button onClick={makeTable} disabled={busy || !chosen.length} className="px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40">실적표 만들기 ({chosen.length})</button>
                </div>
                {table && <a href={table.url} target="_blank" rel="noreferrer" className="block px-3 pt-2 text-xs text-indigo-600 hover:underline">{table.fileName} <ExternalLink size={11} className="inline" /></a>}
                <div className="max-h-72 overflow-y-auto px-3 py-1">
                  {rec.records.filter((r) => !onlyFit || (r.inYears !== false && r.meetsAmount !== false)).map((r) => (
                    <label key={r.documentId} className="flex items-start gap-2 py-1 text-sm border-b border-slate-50">
                      <input type="checkbox" className="mt-1" checked={chosen.includes(r.documentId)}
                        onChange={(e) => setChosen(e.target.checked ? [...chosen, r.documentId] : chosen.filter((x) => x !== r.documentId))} />
                      <span className="min-w-0 flex-1">
                        <span className="text-slate-900">{r.title}</span>
                        <span className="block text-xs text-slate-500">
                          {r.client} · {r.periodStart && r.periodEnd ? `${r.periodStart}~${r.periodEnd}` : r.contractDate ?? '기간 미상'} · {r.supplyAmount != null ? `${Math.round(r.supplyAmount).toLocaleString('ko-KR')}원` : r.amountText}{r.role ? ` · ${r.role}` : ''}
                          {r.inYears === false && <span className="text-red-600"> · 기간 밖</span>}
                          {r.meetsAmount === false && <span className="text-red-600"> · 금액 미달</span>}
                          {r.meetsAmount === null && rec.requirement.minAmount && <span className="text-amber-700"> · 금액 확인</span>}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 cursor-pointer">
                + 직접 준비한 파일 (PDF · JPG · PNG)
                <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { setFiles([...files, ...Array.from(e.target.files ?? [])].slice(0, 20)); e.target.value = ''; }} />
              </label>
              {files.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center gap-1 text-sm text-slate-700">{f.name}
                      <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="p-0.5 rounded text-slate-400 hover:bg-slate-100"><X size={12} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="text-xs text-slate-400">묶는 순서: 회사 서류 → 실적 증빙(계약서) → 직접 올린 파일. 순서 바꾸기는 UI 정리 때 붙입니다.</p>
            <div className="flex items-center justify-end gap-2">
              {done && (
                <span className="mr-auto text-xs">
                  <a href={done.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-0.5">{done.fileName} · {done.pages}쪽 <ExternalLink size={11} /></a>
                  {done.warnings.map((w) => <span key={w} className="block text-amber-700">⚠ {w}</span>)}
                </span>
              )}
              <button onClick={make} disabled={busy || !order.length}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1.5">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <FileStack size={14} />} PDF 로 묶어 드라이브에 올리기 ({order.length})
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
